import { useCallback, useEffect } from "react";
import { filterSearch } from "@/lib/columnFilters";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { DriversClient } from "@/features/resources/DriversClient";
import { listDrivers, resourcesKeys, type DriverDetail, type DriverListItem, type ResourcesSearch } from "@/features/resources/resources.api";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { rememberListSearch } from "@/lib/listState";

export const Route = createFileRoute("/_shell/drivers/")({
  // Resources → Chauffeurs needs tms_drivers of its own: the reason by an
  // unavailability may be private (docs/tms-stamdata.md §9). tms_resources_write makes a
  // driver AFAS does not know (S38) — in the screen, and on the API regardless.
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "tms_drivers")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): ResourcesSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      ...filterSearch(search),
      q: str(search.q),
      branch: str(search.branch),
      isActive: str(search.isActive),
      cursor: str(search.cursor),
      limit: str(search.limit),
      sort: str(search.sort),
      order: str(search.order),
    };
  },
  component: DriversScreen,
});

function DriversScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { session } = Route.useRouteContext();
  const canManage = session.roles.includes("admin") || hasModule(session, "tms_resources_write");

  // The driver card's back link returns to the list as it was left.
  useEffect(() => {
    rememberListSearch("/drivers", search);
  }, [search]);

  const query = useQuery({
    queryKey: resourcesKeys.driverList(search),
    queryFn: () => listDrivers(search),
    placeholderData: keepPreviousData,
  });

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  const openDriver = useCallback(
    (driver: DriverListItem) => {
      navigate({ to: "/drivers/$id", params: { id: driver.id } });
    },
    [navigate],
  );

  const handleCreated = useCallback(
    (driver: DriverDetail) => {
      void queryClient.invalidateQueries({ queryKey: resourcesKeys.drivers, refetchType: "none" });
      queryClient.setQueryData(resourcesKeys.driver(driver.id), driver);
      navigate({ to: "/drivers/$id", params: { id: driver.id } });
    },
    [navigate, queryClient],
  );

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Drivers">
      <DriversClient
        items={query.data?.data ?? []}
        pagination={query.data?.pagination ?? { limit: 50, hasNextPage: false, nextCursor: null }}
        search={search}
        updateParams={updateParams}
        onOpen={openDriver}
        isFetching={query.isFetching}
        isLoading={query.isLoading}
        canManage={canManage}
        onCreated={handleCreated}
        userId={session.userId}
      />
    </div>
  );
}
